/**
 * Phase 23-C -- Quick Capture Modal.
 *
 * Cmd+Shift+K opens; type anything; Enter (or Ctrl+Enter) submits to
 * `POST /api/inbox`. Result lands in the user's Inbox (see /inbox) where
 * they can triage it into a task / note / reminder / meeting agenda item.
 *
 * Non-authenticated users see nothing. The modal listens on both the
 * keyboard shortcut and a `open-quick-capture` custom event so other
 * surfaces (Home tile, Cmd+K palette) can programmatically open it.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2, Package, Send } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/store/auth";
import api from "@/lib/api";

const MAX_LEN = 5000;

export function QuickCaptureModal() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const textRef = useRef(null);
  const user = useAuth((s) => s.user);

  // Cmd/Ctrl+Shift+K opens; Esc closes (Radix handles Esc natively).
  useEffect(() => {
    const handler = (e) => {
      const isCapture = (e.key === "k" || e.key === "K")
        && (e.metaKey || e.ctrlKey) && e.shiftKey;
      if (isCapture) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", handler);
    const evt = () => setOpen(true);
    window.addEventListener("open-quick-capture", evt);
    return () => {
      window.removeEventListener("keydown", handler);
      window.removeEventListener("open-quick-capture", evt);
    };
  }, []);

  useEffect(() => {
    if (!open) { setText(""); return; }
    // Focus the textarea on open.
    const t = setTimeout(() => textRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [open]);

  const submit = useCallback(async () => {
    const body = text.trim();
    if (!body) return;
    if (body.length > MAX_LEN) {
      toast.error(`Capture too long (max ${MAX_LEN} chars)`);
      return;
    }
    setSaving(true);
    try {
      await api.post("/inbox", { body, source: "quick_capture" });
      toast.success("Saved to Inbox", { description: "Triage it whenever you like." });
      setOpen(false);
      // Notify listeners (Home tile) that the count changed.
      window.dispatchEvent(new CustomEvent("inbox-item-captured"));
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }, [text]);

  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); submit(); }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="max-w-xl p-0 overflow-hidden gap-0"
        data-testid="quick-capture-dialog"
      >
        <DialogTitle className="sr-only">Quick capture</DialogTitle>
        <DialogDescription className="sr-only">
          Type any thought, task, or note. It will land in your Inbox for triage later.
        </DialogDescription>
        <div className="px-5 pt-4 pb-3 border-b border-white/[0.06] flex items-center gap-2">
          <Package className="h-4 w-4 text-sky-400" strokeWidth={1.75} />
          <div className="flex flex-col">
            <span className="text-[13px] font-medium tracking-wide">Quick Capture</span>
            <span className="text-[11px] text-muted-foreground">
              Save to Inbox · triage into a task / note / reminder later
            </span>
          </div>
          <kbd className="ml-auto text-[10px] font-mono px-1.5 py-0.5 rounded bg-white/5 border border-white/10">
            ⌘⇧K
          </kbd>
        </div>
        <div className="p-4">
          <Textarea
            ref={textRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            rows={4}
            maxLength={MAX_LEN}
            placeholder="Type anything — I'll save it to your Inbox…"
            data-testid="quick-capture-input"
            className="resize-none text-sm bg-white/[0.03] border-white/10 focus-visible:ring-sky-500/40"
          />
          <div className="flex items-center gap-2 mt-3">
            <span className="text-[11px] text-muted-foreground">
              {text.length}/{MAX_LEN} · Ctrl+Enter to save
            </span>
            <div className="ml-auto flex gap-2">
              <Button
                variant="ghost" size="sm"
                onClick={() => setOpen(false)}
                data-testid="quick-capture-cancel"
              >Cancel</Button>
              <Button
                size="sm" onClick={submit}
                disabled={!text.trim() || saving}
                data-testid="quick-capture-submit"
                className="bg-gradient-to-r from-[var(--brand-blue)] to-[var(--sky)] text-white"
              >
                {saving
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                  : <Send className="h-3.5 w-3.5 mr-1.5" />}
                Save
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default QuickCaptureModal;
